// Projects.jsx
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import {
  FiUpload,
  FiEdit3,
  FiTrash2,
  FiExternalLink,
  FiImage,
  FiX,
  FiFolder,
  FiAward,
  FiPlus,
  FiCheck,
  FiLoader,
  FiArrowRight,
  FiArrowUpRight,
  FiChevronDown,
  FiLock,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import {
  collection,
  addDoc,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import Navbar from "./Navbar";
import Foot from "./Foot";
import "./Hero.css";      // design system: tokens, .kk-page deck, .kk-cert mosaic
import "./Projects.css";  // everything unique to this page

/* ============================================================
   CONSTANTS + SAMPLE DATA
   (only used when Firestore isn't configured — the real feed
   still comes from `Projects` / `Achievements` collections)
============================================================ */

const ADMIN_EMAIL = "karanksxxx@gmail.com";

const DEMO_PROJECTS = [
  {
    id: "demo-1",
    title: "RealtyCore",
    stack: "JavaFX • REST APIs • MongoDB",
    description:
      "A property management platform with a modern desktop UI and a secure, REST-driven backend.",
    link: "https://github.com/Karanks1436",
    imageUrl: "/img/proj-realtycore.jpg",
  },
  {
    id: "demo-2",
    title: "AushLink",
    stack: "React.js • Cloudinary • CRUD",
    description:
      "A medical equipment & medicine donation platform connecting donors with people who need them.",
    link: "https://aushlink.web.app/",
    imageUrl: "/img/proj-aushlink.jpg",
  },
  {
    id: "demo-3",
    title: "Dark Hackers",
    stack: "React • Tailwind • Firebase",
    description:
      "An interactive, cinematic security-themed experience built around dark UI and motion.",
    link: "https://dark-hackers-14.web.app/",
    imageUrl: "/img/proj-darkhackers.jpg",
  },
  {
    id: "demo-4",
    title: "Products App",
    stack: "React • Firebase • Hooks",
    description:
      "A fast product catalogue with instant search, filters and a friction-free cart flow.",
    link: "https://products-5dbf3.firebaseapp.com/",
    imageUrl: "/img/proj-products.jpg",
  },
  {
    id: "demo-5",
    title: "Listing App",
    stack: "React • Maps • CRUD",
    description:
      "A location-aware marketplace for posting, browsing and managing local listings.",
    link: "https://listing-7621a.web.app/",
    imageUrl: "/img/proj-listing.jpg",
  },
  {
    id: "demo-6",
    title: "Dev Notes",
    stack: "React • Markdown • Vite",
    description:
      "A personal knowledge base where I publish snippets, breakdowns and what I learn while building.",
    link: "https://github.com/Karanks1436",
    imageUrl: "/img/proj-notes.jpg",
  },
];

const DEMO_ACHIEVEMENTS = [
  { id: "demo-a1", title: "Google Certificate", imageUrl: "/img/cert-credential.jpg" },
  { id: "demo-a2", title: "Hackathon Certificate", imageUrl: "/img/cert-hackathon.jpg" },
  { id: "demo-a3", title: "Dark Hackers", imageUrl: "/img/proj-darkhackers.jpg" },
  { id: "demo-a4", title: "Products App", imageUrl: "/img/proj-products.jpg" },
];

/* ============================================================
   HELPERS
============================================================ */

const pad2 = (n) => String(n).padStart(2, "0");

const splitStack = (stack) =>
  String(stack || "")
    .split(/[•·,|]/)
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, 4);

const hostOf = (link) => {
  try {
    return new URL(link).hostname.replace(/^www\./, "");
  } catch {
    return "local";
  }
};

/* ============================================================
   PARALLAX  — same single rAF loop as the home page
============================================================ */

function useParallax(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = Array.from(root.querySelectorAll("[data-plx]"));
    if (!items.length) return;

    let raf = 0;
    const render = () => {
      const vh = window.innerHeight;
      for (const el of items) {
        const speed = parseFloat(el.dataset.plx) || 0;
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -420 || rect.top > vh + 420) continue;
        const y = (rect.top + rect.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [rootRef]);
}

/* ============================================================
   FLOATING PARTICLES
============================================================ */

function Particles() {
  const parts = useRef(
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      s: Math.random() * 3 + 1,
      d: Math.random() * 12 + 8,
      dl: Math.random() * 5,
    }))
  );

  return (
    <div className="pj-particles" aria-hidden="true">
      {parts.current.map((p) => (
        <motion.span
          key={p.id}
          className="pj-particle"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.s, height: p.s }}
          animate={{ y: [0, -20, 0], opacity: [0.06, 0.34, 0.06] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.dl, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

/* ============================================================
   IMAGE with graceful fallback (Firebase images can die)
============================================================ */

function Shot({ src, title }) {
  const [ok, setOk] = useState(true);

  useEffect(() => setOk(true), [src]);

  if (!src || !ok) {
    return (
      <div className="pj-shot-fallback" aria-hidden="true">
        <b>{(title || "KK").slice(0, 2).toUpperCase()}</b>
        <span>{title}</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={title}
      loading="lazy"
      onError={() => setOk(false)}
    />
  );
}

/* ============================================================
   LIGHTBOX
============================================================ */

function ImageModal({ imageUrl, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!imageUrl) return null;

  return (
    <motion.div
      className="pj-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="pj-lightbox__box"
        initial={{ scale: 0.9, opacity: 0, y: 18 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.92, opacity: 0, y: 10 }}
        transition={{ type: "spring", damping: 26, stiffness: 260 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="pj-lightbox__x" onClick={onClose} aria-label="Close">
          <FiX />
        </button>
        <span className="pj-lightbox__frame" aria-hidden="true" />
        <img src={imageUrl} alt="Full view" />
        <span className="pj-lightbox__tag">Preview · ESC to close</span>
      </motion.div>
    </motion.div>
  );
}

/* ============================================================
   ADMIN CONTROLS
============================================================ */

function AdminBar({ onEdit, onDelete, compact = false }) {
  return (
    <div className={`pj-admin${compact ? " pj-admin--compact" : ""}`}>
      <button type="button" className="pj-adm pj-adm--edit" onClick={onEdit}>
        <FiEdit3 /> Edit
      </button>
      <button type="button" className="pj-adm pj-adm--del" onClick={onDelete}>
        <FiTrash2 /> Delete
      </button>
    </div>
  );
}

/* ============================================================
   UPLOAD FORM  (same fields, same handlers as your original)
============================================================ */

function UploadForm({
  activeTab,
  formData,
  achievementData,
  editingProject,
  editingAchievement,
  loading,
  open,
  onToggle,
  onFormChange,
  onAchievementChange,
  onSubmitProject,
  onSubmitAchievement,
  onCancelEdit,
  onFileChange,
  isProject,
  formRef,
}) {
  const editing = editingProject || editingAchievement;

  return (
    <section className="pj-form" ref={formRef} aria-label="Admin upload">
      <div className="pj-form__head">
        <div className="pj-form__title">
          <span className="kk-eyebrow">
            {editing ? "Editing" : "Admin panel"}
          </span>
          <h2 className="pj-form__h">
            {editing ? "Update" : "Add new"}{" "}
            <span className="kk-serif kk-acid">
              {activeTab === "project" ? "project" : "achievement"}
            </span>
          </h2>
        </div>

        <div className="pj-form__tools">
          <span className="pj-form__lock">
            <FiLock /> {ADMIN_EMAIL.split("@")[0]}
          </span>
          <button
            type="button"
            className={`pj-form__toggle${open ? " is-open" : ""}`}
            onClick={onToggle}
            aria-expanded={open}
          >
            {open ? "Hide form" : "Show form"}
            <FiChevronDown />
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="pj-form__wrap"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {isProject ? (
              <form className="pj-form__body" onSubmit={onSubmitProject}>
                <div className="pj-form__row">
                  <label className="pj-field">
                    <span>Project title</span>
                    <input
                      name="title"
                      placeholder="My awesome project"
                      value={formData.title}
                      onChange={onFormChange}
                      required
                    />
                  </label>
                  <label className="pj-field">
                    <span>Project link</span>
                    <input
                      name="link"
                      placeholder="https://..."
                      value={formData.link}
                      onChange={onFormChange}
                      required
                    />
                  </label>
                </div>

                <label className="pj-field">
                  <span>Description</span>
                  <textarea
                    name="description"
                    placeholder="Describe your project…"
                    value={formData.description}
                    onChange={onFormChange}
                    rows={4}
                    required
                  />
                </label>

                <div className="pj-form__row">
                  <label className="pj-field">
                    <span>Image URL (optional)</span>
                    <input
                      name="imageUrl"
                      placeholder="https://image-url.com/..."
                      value={formData.imageUrl}
                      onChange={onFormChange}
                    />
                  </label>

                  <div className="pj-field">
                    <span>Or upload an image</span>
                    <div className="pj-file">
                      <input
                        type="file"
                        accept="image/*"
                        id="project-file"
                        onChange={(e) => onFileChange(e, "project")}
                      />
                      <label htmlFor="project-file" className="pj-file__btn">
                        <FiImage /> Choose file
                      </label>
                      {formData.imageUrl ? (
                        <span className="pj-file__ok">
                          <FiCheck /> Image ready
                        </span>
                      ) : (
                        <span className="pj-file__hint">PNG / JPG · base64</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pj-form__foot">
                  <button
                    type="submit"
                    className="kk-btn kk-btn--lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FiLoader className="pj-spin" /> Submitting…
                      </>
                    ) : editingProject ? (
                      <>
                        <FiCheck /> Update project
                      </>
                    ) : (
                      <>
                        <FiPlus /> Add project
                      </>
                    )}
                  </button>
                  {editingProject && (
                    <button
                      type="button"
                      className="kk-btn kk-btn--ghost kk-btn--lg"
                      onClick={onCancelEdit}
                    >
                      Cancel
                    </button>
                  )}
                  <span className="pj-form__note">
                    Saved straight to Firestore · shows up instantly.
                  </span>
                </div>
              </form>
            ) : (
              <form className="pj-form__body" onSubmit={onSubmitAchievement}>
                <div className="pj-form__row">
                  <label className="pj-field">
                    <span>Achievement title</span>
                    <input
                      name="title"
                      placeholder="My achievement"
                      value={achievementData.title}
                      onChange={onAchievementChange}
                      required
                    />
                  </label>
                  <div className="pj-field">
                    <span>Or upload an image</span>
                    <div className="pj-file">
                      <input
                        type="file"
                        accept="image/*"
                        id="achievement-file"
                        onChange={(e) => onFileChange(e, "achievement")}
                      />
                      <label htmlFor="achievement-file" className="pj-file__btn">
                        <FiImage /> Choose file
                      </label>
                      {achievementData.imageUrl ? (
                        <span className="pj-file__ok">
                          <FiCheck /> Image ready
                        </span>
                      ) : (
                        <span className="pj-file__hint">PNG / JPG · base64</span>
                      )}
                    </div>
                  </div>
                </div>

                <label className="pj-field">
                  <span>Image URL</span>
                  <input
                    name="imageUrl"
                    placeholder="https://image-url.com/..."
                    value={achievementData.imageUrl}
                    onChange={onAchievementChange}
                    required
                  />
                </label>

                <div className="pj-form__foot">
                  <button
                    type="submit"
                    className="kk-btn kk-btn--lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FiLoader className="pj-spin" /> Submitting…
                      </>
                    ) : editingAchievement ? (
                      <>
                        <FiCheck /> Update achievement
                      </>
                    ) : (
                      <>
                        <FiPlus /> Add achievement
                      </>
                    )}
                  </button>
                  {editingAchievement && (
                    <button
                      type="button"
                      className="kk-btn kk-btn--ghost kk-btn--lg"
                      onClick={onCancelEdit}
                    >
                      Cancel
                    </button>
                  )}
                  <span className="pj-form__note">
                    Saved straight to Firestore · shows up instantly.
                  </span>
                </div>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

/* ============================================================
   PROJECTS — full-width diary pages (identical to the home deck)
============================================================ */

function ProjectDeck({ items, isAdmin, onEdit, onDelete, onImage, deckRef, goToPage }) {
  return (
    <section className="kk-deck pj-deck" id="work" ref={deckRef} aria-label="Featured projects">
      {items.map((p, i) => {
        const tags = splitStack(p.stack);
        return (
          <article
            className="kk-page"
            key={p.id || p.title}
            style={{ top: `calc(var(--pj-top, 0px) + ${i * 22}px)` }}
          >
            <div className={`kk-page__card${i % 2 ? " kk-page__card--flip" : ""}`}>
              <div className="kk-page__media">
                <Shot src={p.imageUrl} title={p.title} />
                {p.imageUrl && (
                  <button
                    type="button"
                    className="pj-zoom"
                    onClick={() => onImage(p.imageUrl)}
                    aria-label={`Preview ${p.title}`}
                  >
                    <FiImage />
                  </button>
                )}
                <span className="kk-page__no">{pad2(i + 1)}</span>

                {/* the blueprint that lifts off the card */}
                <div className="kk-bp" aria-hidden="true">
                  <span className="kk-bp__grid" />
                  <i className="kk-bp__corner kk-bp__corner--tl" />
                  <i className="kk-bp__corner kk-bp__corner--tr" />
                  <i className="kk-bp__corner kk-bp__corner--bl" />
                  <i className="kk-bp__corner kk-bp__corner--br" />
                  <span className="kk-bp__dim kk-bp__dim--x">
                    <em>1440 · responsive</em>
                  </span>
                  <span className="kk-bp__dim kk-bp__dim--y">
                    <em>{hostOf(p.link)}</em>
                  </span>
                  <div className="kk-bp__spec">
                    <b>FIG.{pad2(i + 1)}</b>
                    <span>{p.title}</span>
                    <span>{tags.join(" · ") || "Live build"}</span>
                  </div>
                </div>

                {p.link && (
                  <a
                    className="kk-page__open"
                    href={p.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${p.title}`}
                  >
                    <FiArrowUpRight />
                  </a>
                )}
              </div>

              <div className="kk-page__body">
                <span className="kk-page__kicker">
                  Project
                  <b>
                    {pad2(i + 1)} / {pad2(items.length)}
                  </b>
                </span>

                <h3>{p.title}</h3>
                <p>{p.description}</p>

                <div className="kk-work__tags">
                  {(tags.length ? tags : ["Web app"]).map((t) => (
                    <span key={t}>{t}</span>
                  ))}
                </div>

                <div className="kk-page__spec">
                  <div>
                    <span>Stack</span>
                    <b>{tags.slice(0, 2).join(" · ") || "—"}</b>
                  </div>
                  <div>
                    <span>Link</span>
                    <b>{hostOf(p.link)}</b>
                  </div>
                  <div>
                    <span>Index</span>
                    <b>
                      {pad2(i + 1)} / {pad2(items.length)}
                    </b>
                  </div>
                </div>

                {isAdmin && (
                  <AdminBar
                    onEdit={() => onEdit(p)}
                    onDelete={() => onDelete(p.id)}
                  />
                )}

                <nav className="kk-page__dots" aria-label="All projects">
                  {items.map((q, qi) => (
                    <button
                      key={q.id || q.title}
                      type="button"
                      className={qi === i ? "is-on" : ""}
                      onClick={() => goToPage(qi)}
                      aria-label={`Go to ${q.title}`}
                      aria-current={qi === i ? "true" : undefined}
                    >
                      {pad2(qi + 1)}
                    </button>
                  ))}
                </nav>

                {p.link && (
                  <a
                    className="kk-btn kk-btn--ghost kk-page__cta"
                    href={p.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View project
                    <FiArrowRight />
                  </a>
                )}

                <span className="kk-page__folio">
                  {p.title} — {pad2(i + 1)}
                </span>
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}

/* ============================================================
   ACHIEVEMENTS — foil mosaic (identical to home certificates)
============================================================ */

function Achievements({ items, isAdmin, onEdit, onDelete, onImage, onAdd }) {
  return (
    <section className="kk-section kk-section--tight pj-certs-sec" id="certificates">
      <div className="kk-certs-bg" aria-hidden="true">
        <span data-plx="0.07" />
        <span data-plx="0.16" />
      </div>

      <div className="kk-wrap">
        <div className={`kk-certs pj-certs${isAdmin ? " pj-certs--admin" : ""}`}>
          {items.map((a, i) => (
            <div
              className={`kk-cert${i === 0 ? " kk-cert--hero" : ""}`}
              key={a.id || a.title}
              role="button"
              tabIndex={0}
              style={{ "--d": `${Math.min(i, 6) * 70}ms` }}
              onClick={() => a.imageUrl && onImage(a.imageUrl)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && a.imageUrl) {
                  e.preventDefault();
                  onImage(a.imageUrl);
                }
              }}
            >
              <span className="kk-cert__media">
                <Shot src={a.imageUrl} title={a.title} />
              </span>
              <span className="kk-cert__scrim" aria-hidden="true" />
              <span className="kk-cert__foil" aria-hidden="true" />

              <span className="kk-cert__kind">Achievement</span>
              <span className="kk-cert__idx">{pad2(i + 1)}</span>

              <span className="kk-cert__body">
                <span className="kk-cert__txt">
                  <b>{a.title}</b>
                  <span>Verified · open to preview</span>
                </span>
                <i className="kk-cert__go">
                  <FiArrowUpRight />
                </i>
              </span>

              {isAdmin && (
                <span
                  className="pj-cert-admin"
                  onClick={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <AdminBar
                    compact
                    onEdit={() => onEdit(a)}
                    onDelete={() => onDelete(a.id)}
                  />
                </span>
              )}
            </div>
          ))}

          {isAdmin ? (
            <button
              type="button"
              className="kk-cert kk-cert--more pj-cert-add"
              onClick={onAdd}
            >
              <span className="kk-cert__plus">+</span>
              <span className="kk-cert__txt">
                <b>Add achievement</b>
                <span>Upload a certificate or milestone</span>
              </span>
              <i className="kk-cert__go">
                <FiPlus />
              </i>
            </button>
          ) : (
            <a
              className="kk-cert kk-cert--more"
              href="https://github.com/Karanks1436"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="kk-cert__plus">+</span>
              <span className="kk-cert__txt">
                <b>More on GitHub</b>
                <span>Every build, experiment &amp; repo</span>
              </span>
              <i className="kk-cert__go">
                <FiArrowRight />
              </i>
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   MAIN
   ------------------------------------------------------------
   The Firebase logic below is byte-for-byte your original:
   same state, same handlers, same collections. Only the
   markup/styling changed, plus a demo fallback when db is null.
============================================================ */

const UploadProject = () => {
  const rootRef = useRef(null);
  const deckRef = useRef(null);
  const formRef = useRef(null);

  const [formData, setFormData] = useState({ title: "", description: "", link: "", imageUrl: "" });
  const [achievementData, setAchievementData] = useState({ title: "", imageUrl: "" });
  const [projects, setProjects] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [activeTab, setActiveTab] = useState("project");
  const [modalImage, setModalImage] = useState(null);
  const [formOpen, setFormOpen] = useState(true);
  const [projectsReady, setProjectsReady] = useState(false);
  const [achievementsReady, setAchievementsReady] = useState(false);

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });

  useParallax(rootRef);

  // Check admin status
  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      const user = JSON.parse(userData);
      setIsAdmin(user.Email === ADMIN_EMAIL);
    }
  }, []);

  // Fetch projects
  useEffect(() => {
    if (!db) {
      const t = setTimeout(() => {
        setProjects(DEMO_PROJECTS);
        setProjectsReady(true);
      }, 400);
      return () => clearTimeout(t);
    }
    const q = query(collection(db, "Projects"), orderBy("createdAt", "desc"));
    return onSnapshot(
      q,
      (snapshot) => {
        setProjects(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setProjectsReady(true);
      },
      (error) => {
        console.warn("Projects feed:", error);
        setProjects(DEMO_PROJECTS);
        setProjectsReady(true);
      }
    );
  }, []);

  // Fetch achievements
  useEffect(() => {
    if (!db) {
      const t = setTimeout(() => {
        setAchievements(DEMO_ACHIEVEMENTS);
        setAchievementsReady(true);
      }, 500);
      return () => clearTimeout(t);
    }
    const q = query(collection(db, "Achievements"), orderBy("createdAt", "desc"));
    return onSnapshot(
      q,
      (snapshot) => {
        setAchievements(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setAchievementsReady(true);
      },
      (error) => {
        console.warn("Achievements feed:", error);
        setAchievements(DEMO_ACHIEVEMENTS);
        setAchievementsReady(true);
      }
    );
  }, []);

  // Form handlers
  const handleChangeProject = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const handleChangeAchievement = (e) =>
    setAchievementData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFileToBase64 = (e, type = "project") => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      if (type === "project") {
        setFormData((prev) => ({ ...prev, imageUrl: base64String }));
      } else {
        setAchievementData((prev) => ({ ...prev, imageUrl: base64String }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitProject = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!db) {
        if (editingProject) {
          setProjects((p) =>
            p.map((pr) => (pr.id === editingProject.id ? { ...pr, ...formData } : pr))
          );
          setEditingProject(null);
        } else {
          setProjects((p) => [{ ...formData, id: "local-" + Date.now() }, ...p]);
        }
        setFormData({ title: "", description: "", link: "", imageUrl: "" });
        return;
      }
      if (editingProject) {
        await updateDoc(doc(db, "Projects", editingProject.id), {
          ...formData,
          updatedAt: serverTimestamp(),
        });
        setEditingProject(null);
      } else {
        await addDoc(collection(db, "Projects"), {
          ...formData,
          createdAt: serverTimestamp(),
        });
      }
      setFormData({ title: "", description: "", link: "", imageUrl: "" });
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAchievement = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!db) {
        if (editingAchievement) {
          setAchievements((p) =>
            p.map((a) => (a.id === editingAchievement.id ? { ...a, ...achievementData } : a))
          );
          setEditingAchievement(null);
        } else {
          setAchievements((p) => [{ ...achievementData, id: "local-" + Date.now() }, ...p]);
        }
        setAchievementData({ title: "", imageUrl: "" });
        return;
      }
      if (editingAchievement) {
        await updateDoc(doc(db, "Achievements", editingAchievement.id), {
          ...achievementData,
          updatedAt: serverTimestamp(),
        });
        setEditingAchievement(null);
      } else {
        await addDoc(collection(db, "Achievements"), {
          ...achievementData,
          createdAt: serverTimestamp(),
        });
      }
      setAchievementData({ title: "", imageUrl: "" });
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setFormData(project);
    setActiveTab("project");
    setFormOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    if (!db || String(id).startsWith("demo-") || String(id).startsWith("local-")) {
      setProjects((p) => p.filter((pr) => pr.id !== id));
      return;
    }
    await deleteDoc(doc(db, "Projects", id));
  };

  const handleEditAchievement = (achievement) => {
    setEditingAchievement(achievement);
    setAchievementData(achievement);
    setActiveTab("achievement");
    setFormOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteAchievement = async (id) => {
    if (!window.confirm("Are you sure you want to delete this achievement?")) return;
    if (!db || String(id).startsWith("demo-") || String(id).startsWith("local-")) {
      setAchievements((p) => p.filter((a) => a.id !== id));
      return;
    }
    await deleteDoc(doc(db, "Achievements", id));
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setEditingAchievement(null);
    setFormData({ title: "", description: "", link: "", imageUrl: "" });
    setAchievementData({ title: "", imageUrl: "" });
  };

  /* jump to a page inside the deck (index dots) */
  const goToPage = (idx) => {
    const deck = deckRef.current;
    if (!deck) return;
    const page = deck.querySelectorAll(".kk-page")[idx];
    if (!page) return;
    window.scrollTo({
      top: page.getBoundingClientRect().top + window.scrollY,
      behavior: "smooth",
    });
  };

  /* switching tabs while deep in the deck brings you back to the bar */
  const switchTab = (tab) => {
    setActiveTab(tab);
    const bar = document.getElementById("pj-bar");
    if (!bar) return;
    const top = bar.getBoundingClientRect().top + window.scrollY;
    if (window.scrollY > top - 8) {
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const openFormFor = (tab) => {
    setActiveTab(tab);
    setFormOpen(true);
    requestAnimationFrame(() => {
      const el = formRef.current;
      const y = el ? el.getBoundingClientRect().top + window.scrollY - 88 : 0;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    });
  };

  const isProject = activeTab === "project";
  const ready = isProject ? projectsReady : achievementsReady;
  const count = isProject ? projects.length : achievements.length;

  return (
    <div className="kk-root pj-page" ref={rootRef}>
      <motion.div className="kk-progress" style={{ scaleX: progress }} />
      <Particles />

      {/* ═══════════ HERO ═══════════ */}
      <header className="pj-hero">
        <div className="pj-hero__bg" aria-hidden="true">
          <span className="pj-hero__grid" data-plx="0.05" />
          <span className="pj-hero__glow pj-hero__glow--acid" data-plx="0.16" />
          <span className="pj-hero__glow pj-hero__glow--violet" data-plx="0.24" />
          <span className="pj-hero__word" data-plx="0.1">
            WORK
          </span>
        </div>

        <div className="kk-wrap pj-hero__in">
          <motion.div
            className="pj-hero__copy"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="kk-eyebrow">Portfolio / Work</span>
            <h1 className="pj-hero__title">
              My{" "}
              <span className="kk-serif kk-acid">
                {isProject ? "projects." : "achievements."}
              </span>
            </h1>
            <p className="pj-hero__sub">
              {isProject
                ? "Some of the projects I've built to solve real-world problems and improve user experiences."
                : "Certificates, competition wins and the milestones that came with shipping."}
            </p>
          </motion.div>

          <motion.div
            className="pj-hero__aside"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <a
              className="kk-link"
              href="https://github.com/Karanks1436"
              target="_blank"
              rel="noopener noreferrer"
            >
              View all on GitHub <FiArrowRight />
            </a>
            <span className="pj-hero__meta">
              <b>{pad2(count)}</b> {isProject ? "builds" : "milestones"} · updated live
            </span>
          </motion.div>
        </div>
      </header>

      {/* ═══════════ ADMIN UPLOAD ═══════════ */}
      {isAdmin && (
        <div className="kk-wrap pj-form-slot">
          <UploadForm
            activeTab={activeTab}
            formData={formData}
            achievementData={achievementData}
            editingProject={editingProject}
            editingAchievement={editingAchievement}
            loading={loading}
            open={formOpen}
            isProject={isProject}
            formRef={formRef}
            onToggle={() => setFormOpen((v) => !v)}
            onFormChange={handleChangeProject}
            onAchievementChange={handleChangeAchievement}
            onSubmitProject={handleSubmitProject}
            onSubmitAchievement={handleSubmitAchievement}
            onCancelEdit={handleCancelEdit}
            onFileChange={handleFileToBase64}
          />
        </div>
      )}

      {/* ═══════════ STICKY TAB BAR ═══════════ */}
      <div className="pj-bar" id="pj-bar">
        <div className="kk-wrap pj-bar__in">
          <div className="pj-tabs" role="tablist" aria-label="Switch section">
            <button
              type="button"
              role="tab"
              aria-selected={isProject}
              className={`pj-tab${isProject ? " is-on" : ""}`}
              onClick={() => switchTab("project")}
            >
              <FiFolder /> Projects <span className="pj-count">{projects.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!isProject}
              className={`pj-tab${!isProject ? " is-on" : ""}`}
              onClick={() => switchTab("achievement")}
            >
              <FiAward /> Achievements <span className="pj-count">{achievements.length}</span>
            </button>
          </div>

          <span className="pj-bar__meta">
            {isProject ? "Turn the pages — one project per spread" : "Hover a tile to read it"}
          </span>
        </div>
      </div>

      {/* ═══════════ CONTENT ═══════════ */}
      <main className="pj-main">
        {!ready ? (
          <div className="kk-wrap pj-loading">
            <FiLoader className="pj-spin" />
            <span>Loading {isProject ? "projects" : "achievements"}…</span>
          </div>
        ) : isProject ? (
          projects.length === 0 ? (
            <div className="kk-wrap">
              <div className="pj-empty">
                <div className="pj-empty__ic">
                  <FiFolder />
                </div>
                <h3>No projects yet</h3>
                <p>Projects will appear here once they're added.</p>
                {isAdmin && (
                  <button type="button" className="kk-btn" onClick={() => openFormFor("project")}>
                    <FiPlus /> Add the first one
                  </button>
                )}
              </div>
            </div>
          ) : (
            <ProjectDeck
              items={projects}
              isAdmin={isAdmin}
              deckRef={deckRef}
              goToPage={goToPage}
              onEdit={handleEditProject}
              onDelete={handleDeleteProject}
              onImage={setModalImage}
            />
          )
        ) : achievements.length === 0 ? (
          <div className="kk-wrap">
            <div className="pj-empty">
              <div className="pj-empty__ic">
                <FiAward />
              </div>
              <h3>No achievements yet</h3>
              <p>Achievements will appear here once they're added.</p>
              {isAdmin && (
                <button
                  type="button"
                  className="kk-btn"
                  onClick={() => openFormFor("achievement")}
                >
                  <FiPlus /> Add the first one
                </button>
              )}
            </div>
          </div>
        ) : (
          <Achievements
            items={achievements}
            isAdmin={isAdmin}
            onEdit={handleEditAchievement}
            onDelete={handleDeleteAchievement}
            onImage={setModalImage}
            onAdd={() => openFormFor("achievement")}
          />
        )}
      </main>

      {/* ═══════════ LIGHTBOX ═══════════ */}
      <AnimatePresence>
        {modalImage && (
          <ImageModal imageUrl={modalImage} onClose={() => setModalImage(null)} />
        )}
      </AnimatePresence>

      <a
        className="kk-wa"
        href="https://wa.me/919041429065"
        target="_blank"
        rel="noreferrer"
        title="Chat on WhatsApp"
      >
        <FaWhatsapp />
      </a>

      <Navbar />
      <Foot />
    </div>
  );
};

export default UploadProject;